import React, { useCallback, useState } from 'react';
import cn from 'clsx';

import Collapsible from '../../../../library/collapsible/collapsible';
import Image from '../../../../library/image/image';
import Typography from '../../../../library/typography/typography';
import SummaryBar from '../../../../components/summary-bar/summary-bar';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';
import VehicleImage from '../../../../components/vehicle-image/vehicle-image';
import VehicleSummaryBar from '../../../../components/vehicle-summary-bar/vehicle-summary-bar';
import FitmentBanner from '../../../../library/fitment/components/fitment-banner/fitment-banner';

import {
  useFitment,
  useModal,
  useMyAccount,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import { SHOP_TIRES_FLOW_MODAL } from '../../../../shared/constants/modals';

import styles from './product-list-profile-chips.scss';

interface ProductListProfileChipsProps {
  showCollapsibleFitmentBanner?: boolean;
  showTreadwellProfile: boolean;
}

function ProductListProfileChips(props: ProductListProfileChipsProps) {
  const { showCollapsibleFitmentBanner = false, showTreadwellProfile } = props;
  const [isCollapsible, setIsCollapsible] = useState(false);
  const { partialFitment, selectedFitment } = useFitment();
  const { isFleetAccount, user } = useMyAccount();
  const { openModal } = useModal();
  const { treadwellPreferences } = useTreadwellPreferences();

  const { selectedDriverType, zipCodeWeatherData } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};

  const fitment = selectedFitment || partialFitment;
  const { make, model, smallImageUrl, trim, year } = fitment || {};

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const companyName = isFleetTreadwell && user?.companyName;

  const defaultEditFitment = useCallback(() => {
    window.location.hash = '#/fitment/vehicle/edit/confirm';
  }, []);

  return (
    <div
      className={cn(
        styles['container'],
        'product-list-profile-chips',
        !showTreadwellProfile ? 'product-list-profile-chips--no-margin' : ''
      )}
    >
      {showCollapsibleFitmentBanner ? (
        fitment && (
          <Collapsible
            isOpen={isCollapsible}
            label={
              isCollapsible ? (
                <FitmentBanner
                  className={styles['fitment-banner']}
                  fitment={fitment}
                  hideDelete
                  isPartialFitment={!selectedFitment && partialFitment}
                  onEditFitment={() => {
                    defaultEditFitment();
                    trackAction('vehicle_page_edit');
                  }}
                  showChangeVehicleLink
                  showEditLink
                  showLicenseVin={isFleetEnabled}
                />
              ) : (
                <div className={styles['banner-container']}>
                  <VehicleImage
                    alt="vehicle"
                    className={styles['vehicle-image']}
                    height="31"
                    smallImageUrl={smallImageUrl}
                    width="70"
                  />
                  <div className={styles['vehicle-description']}>
                    <strong>
                      {year} {make}
                    </strong>{' '}
                    {model} {trim}
                  </div>
                </div>
              )
            }
            onToggle={() => setIsCollapsible(!isCollapsible)}
            toggleType="CHEVRON"
          />
        )
      ) : (
        <VehicleSummaryBar
          fitment={selectedFitment}
          onClick={() => {
            defaultEditFitment();
            trackAction('vehicle_page_edit');
          }}
        />
      )}

      {showTreadwellProfile && (
        <SummaryBar
          className={styles['tw-banner']}
          onClick={() =>
            openModal(SHOP_TIRES_FLOW_MODAL, {
              hideZipBackButton: true,
              shouldStartAtTreadwell: true
            })
          }
        >
          <Image
            alt="Treadwell"
            className={styles['treadwell-icon']}
            height="24px"
            src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
          />

          <Typography
            className={styles['driver-type']}
            tag="span"
            weight="bold"
          >
            {name && (
              <TreadwellPersonaIcon
                className={styles['persona-icon']}
                iconName={icon}
              />
            )}{' '}
            {companyName || name || 'Custom'}
          </Typography>

          <Typography tag="span">
            <span className={styles['city']}>{city?.toLowerCase()}</span>,{' '}
            {state}
          </Typography>
        </SummaryBar>
      )}
    </div>
  );
}

export default ProductListProfileChips;
